import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Star, LogOut, X, Sun, Moon, Wifi, WifiOff, ChevronDown, ChevronRight } from "lucide-react";
import EduLogo from "./EduLogo";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useRealtime } from "../context/RealtimeContext";
import { api } from "../lib/api";

export default function Sidebar({ items }) {
    const initialMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const [open, setOpen] = useState(!initialMobile);
    const [isMobile, setIsMobile] = useState(initialMobile);
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const { unread, connected } = useRealtime();
    const location = useLocation();
    const navigate = useNavigate();

    const [courses, setCourses] = useState([]);
    const [coursesLoaded, setCoursesLoaded] = useState(false);
    const [coursesOpen, setCoursesOpen] = useState(false);
    const hasExpandableCourses = items.some((it) => it.expandable === "courses");

    useEffect(() => {
        if (!hasExpandableCourses || coursesLoaded) return;
        api.get("/courses")
            .then((r) => setCourses(r.data || []))
            .catch(() => {})
            .finally(() => setCoursesLoaded(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasExpandableCourses]);

    useEffect(() => {
        if (location.pathname.startsWith("/profesor/cursos/")) setCoursesOpen(true);
        if (location.pathname === "/alumno/lecciones" && new URLSearchParams(location.search).get("course")) {
            setCoursesOpen(true);
        }
    }, [location.pathname, location.search]);

    function courseLink(c) {
        if (user?.role === "alumno") return `/alumno/lecciones?course=${encodeURIComponent(c.name)}`;
        return `/profesor/cursos/${c.id}`;
    }

    function isCourseActive(c) {
        if (user?.role === "alumno") {
            return (
                location.pathname === "/alumno/lecciones" &&
                new URLSearchParams(location.search).get("course") === c.name
            );
        }
        return location.pathname === `/profesor/cursos/${c.id}`;
    }

    // Submenús estáticos (ej. "Gestión de Usuarios" -> Datos del alumno / Familia / Médico)
    const [staticOpen, setStaticOpen] = useState({});
    useEffect(() => {
        items.forEach((it) => {
            if (it.children?.some((c) => location.pathname.startsWith(c.to))) {
                setStaticOpen((prev) => (prev[it.to] ? prev : { ...prev, [it.to]: true }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        function onResize() {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setOpen(true);
        }
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (isMobile) setOpen(false);
    }, [location.pathname]);

    const widthClass = isMobile
        ? open ? "w-64" : "w-0"
        : open ? "w-64" : "w-[76px]";
    const positionClass = isMobile
        ? "fixed inset-y-0 left-0 z-40"
        : "relative";

    return (
        <>
            {isMobile && !open && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed top-3 left-3 z-30 p-2 rounded-xl bg-edu-card border border-edu shadow-md text-edu-navy"
                    data-testid="mobile-sidebar-open"
                    aria-label="Abrir menú"
                >
                    <Menu size={20} />
                </button>
            )}
            {isMobile && open && (
                <div
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 bg-black/40 z-30"
                    data-testid="sidebar-backdrop"
                    aria-hidden
                />
            )}
            <aside
                aria-label="Menú principal"
                className={`bg-edu-card border-r border-edu shadow-sm transition-all duration-300 flex flex-col overflow-hidden ${positionClass} ${widthClass}`}
                data-testid="sidebar"
            >
                <div className="px-4 py-5 flex items-center justify-between border-b border-edu">
                    {open ? (
                        <EduLogo size="sm" />
                    ) : (
                        <div className="w-9 h-9 rounded-xl bg-edu-navy flex items-center justify-center mx-auto">
                            <Star size={16} className="text-edu-gold" fill="currentColor" />
                        </div>
                    )}
                    <button
                        onClick={() => setOpen(!open)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-edu-navy"
                        data-testid="sidebar-toggle"
                        aria-label={open ? "Colapsar menú" : "Expandir menú"}
                        aria-expanded={open}
                    >
                        {open ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
                    </button>
                </div>

                <nav aria-label="Navegación" className="flex-1 overflow-y-auto edu-scroll p-3 space-y-1">
                    {items.map((it) => {
                        const Icon = it.icon;
                        const active = location.pathname === it.to || location.pathname.startsWith(it.to + "/");
                        const isMessaging = it.testId === "mensajeria";
                        const isCourses = it.expandable === "courses";
                        const hasStaticChildren = Array.isArray(it.children) && it.children.length > 0;
                        return (
                            <div key={it.to}>
                                <div className="flex items-center gap-1">
                                    <Link
                                        to={it.to}
                                        data-testid={`sidebar-${it.testId}-link`}
                                        aria-current={active ? "page" : undefined}
                                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors relative flex-1 min-w-0 ${
                                            active
                                                ? "bg-edu-navy text-white shadow-sm"
                                                : "text-edu-text hover:bg-gray-100"
                                        } ${!open ? "justify-center" : ""}`}
                                    >
                                        <Icon size={18} className={active ? "text-edu-gold" : "text-edu-navy"} aria-hidden />
                                        {open && <span className="truncate flex-1">{it.label}</span>}
                                        {isMessaging && unread > 0 && (
                                            <span
                                                className={`${open ? "" : "absolute top-1 right-1"} inline-flex items-center justify-center bg-edu-gold text-[#0A0A0A] text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5`}
                                                data-testid="sidebar-unread-badge"
                                                aria-label={`${unread} mensajes no leídos`}
                                            >
                                                {unread > 99 ? "99+" : unread}
                                            </span>
                                        )}
                                    </Link>
                                    {isCourses && open && courses.length > 0 && (
                                        <button
                                            onClick={() => setCoursesOpen((v) => !v)}
                                            className="p-1.5 rounded-lg hover:bg-gray-100 text-edu-muted flex-shrink-0"
                                            aria-label={coursesOpen ? "Ocultar lista de cursos" : "Mostrar lista de cursos"}
                                            aria-expanded={coursesOpen}
                                            data-testid="sidebar-mis-cursos-toggle"
                                        >
                                            {coursesOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </button>
                                    )}
                                    {hasStaticChildren && open && (
                                        <button
                                            onClick={() => setStaticOpen((prev) => ({ ...prev, [it.to]: !prev[it.to] }))}
                                            className="p-1.5 rounded-lg hover:bg-gray-100 text-edu-muted flex-shrink-0"
                                            aria-label={staticOpen[it.to] ? "Ocultar opciones" : "Mostrar opciones"}
                                            aria-expanded={!!staticOpen[it.to]}
                                            data-testid={`sidebar-${it.testId}-toggle`}
                                        >
                                            {staticOpen[it.to] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </button>
                                    )}
                                </div>
                                {hasStaticChildren && open && staticOpen[it.to] && (
                                    <div className="mt-1 ml-4 pl-3 border-l border-edu space-y-0.5">
                                        {it.children.map((c) => {
                                            const childActive = location.pathname.startsWith(c.to);
                                            return (
                                                <Link
                                                    key={c.to}
                                                    to={c.to}
                                                    className={`flex items-center px-2.5 py-1.5 rounded-lg text-xs transition-colors truncate ${
                                                        childActive ? "bg-edu-soft text-edu-navy font-medium" : "text-edu-muted hover:bg-gray-100"
                                                    }`}
                                                >
                                                    <span className="truncate">{c.label}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                                {isCourses && open && coursesOpen && courses.length > 0 && (
                                    <div className="mt-1 ml-4 pl-3 border-l border-edu space-y-0.5">
                                        {courses.map((c) => {
                                            const courseActive = isCourseActive(c);
                                            return (
                                                <Link
                                                    key={c.id}
                                                    to={courseLink(c)}
                                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-colors truncate ${
                                                        courseActive ? "bg-edu-soft text-edu-navy font-medium" : "text-edu-muted hover:bg-gray-100"
                                                    }`}
                                                >
                                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.color || "#1B2F6E" }} />
                                                    <span className="truncate">{c.name}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                <div className="border-t border-edu p-3 space-y-2">
                    <button
                        onClick={toggle}
                        aria-label={theme === "dark" ? "Activar modo claro" : "Activar modo oscuro"}
                        aria-pressed={theme === "dark"}
                        data-testid="sidebar-theme-toggle"
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-edu-text hover:bg-gray-100 ${!open ? "justify-center" : ""}`}
                    >
                        {theme === "dark" ? (
                            <Sun size={16} className="text-edu-gold" aria-hidden />
                        ) : (
                            <Moon size={16} className="text-edu-navy" aria-hidden />
                        )}
                        {open && <span>{theme === "dark" ? "Modo claro" : "Modo oscuro"}</span>}
                    </button>

                    <button
                        onClick={() => navigate("/help")}
                        data-testid="sidebar-help-button"
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-edu-text hover:bg-gray-100 ${!open ? "justify-center" : ""}`}
                    >
                        <Star size={18} className="text-edu-gold" fill="currentColor" aria-hidden />
                        {open && <span>Centro de Ayuda</span>}
                    </button>

                    <button
                        onClick={async () => { await logout(); navigate("/login"); }}
                        data-testid="sidebar-logout-button"
                        aria-label="Cerrar sesión"
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold bg-edu-navy text-white hover:opacity-90 ${!open ? "justify-center" : ""}`}
                    >
                        <LogOut size={16} aria-hidden />
                        {open && <span>Cerrar Sesión</span>}
                    </button>

                    {open && (
                        <div className="text-center text-[10px] text-edu-muted pt-1 flex items-center justify-center gap-1.5" data-testid="sidebar-version">
                            <span>Grit v1.0.0</span>
                            <span aria-hidden>·</span>
                            {connected ? (
                                <span title="Conectado en tiempo real" className="inline-flex items-center gap-0.5 text-emerald-600">
                                    <Wifi size={10} /> en vivo
                                </span>
                            ) : (
                                <span title="Sin conexión en tiempo real" className="inline-flex items-center gap-0.5 text-edu-muted">
                                    <WifiOff size={10} /> offline
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
